"use client";

import {
  TextField, Button, MenuItem, Typography, Box, Grid, Divider,
  Chip, Dialog, DialogContent, Avatar, Checkbox, FormControlLabel,
  FormControl, InputLabel, Select, Alert, Switch,
} from "@mui/material";
import FlagIcon from "@mui/icons-material/Flag";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import PersonIcon from "@mui/icons-material/Person";
import { useEffect, useState } from "react";
import { axiosRequest } from "../../utility/axios";
import { useAxiosFetch } from "../../hooks/useAxiosFetch";
import { getSweetAlert } from "../../helper";
import Swal from "sweetalert2";
import { apiBaseUrl } from "../../constant/endpoint";
import ChipIssueForm from "../chipIssue/chipIssueForm";
import RedFlagPopup from "../../components/dialog/redFlagPopup";
import FloatPopup from "../../components/dialog/floatPopup";

export default function DepositDialog({ open, onClose, initialUser, token, onSuccess, initialData }) {
  const { data: chipDenomination, refetch: chipDenominationRefetch } = useAxiosFetch("GET", "/chips", token);
  const { data: tourChipDenomination, refetch: tourChipDenominationRefetch } = useAxiosFetch("GET", "/tournament_chips", token);
  const { data: tournaments } = useAxiosFetch("GET", "/tournament", token);
  const { data: offers } = useAxiosFetch("GET", "/offers", token);

  const [depositCategory, setDepositCategory] = useState("simple");
  const [selectedTournament, setSelectedTournament] = useState("");
  const [tournamentType, setTournamentType] = useState("fee");
  const [selectedOffer, setSelectedOffer] = useState("");
  const [isFirstDepositor, setIsFirstDepositor] = useState(true);
  const [type, setType] = useState("cash");
  const [amount, setAmount] = useState("");
  const [utr, setUtr] = useState("");
  const [confirmBtn, setConfirmBtn] = useState(false);
  const [cashback, setCashback] = useState(0);
  const [maxAllowedChips, setMaxAllowedChips] = useState(0);
  const [offerError, setOfferError] = useState("");
  const [isRefer, setIsRefer] = useState(false);
  const [referralCode, setReferralCode] = useState("");
  const [userIsReferred, setUserIsReferred] = useState(false);
  const [lastOfferAt, setLastOfferAt] = useState(null);
  const [offerApproved, setOfferApproved] = useState(false);
  const [chipDenominations, setChipDenominations] = useState({});
  const [chipIssueOpen, setChipIssueOpen] = useState(false);
  const [chipIssueUser, setChipIssueUser] = useState(null);
  const [chipIssueAmount, setChipIssueAmount] = useState(0);
  const [liveUser, setLiveUser] = useState(null);
  const [remark, setRemark] = useState("");
  const [repayFloat, setRepayFloat] = useState(true);
  const [accounts, setAccounts] = useState([]);
  const [selectedAccountId, setSelectedAccountId] = useState("");

  const totalChips = Object.keys(chipDenominations).reduce((s, k) => s + k * chipDenominations[k], 0);
  const totalCash = Number(amount) || 0;
  const floatDue = Number((liveUser || initialUser)?.float_outstanding || 0);

  // Pre-fill from transaction data when dialog opens
  useEffect(() => {
    if (!open || !initialData) return;
    const mode = (initialData.payment_mode || "CASH").toLowerCase();
    setType(mode === "bank" ? "bank" : mode === "wallet" ? "wallet" : mode === "other" ? "other" : "cash");
    setDepositCategory(initialData.depositCategory || "simple");
    setAmount(initialData.txn_amount ? String(initialData.txn_amount) : "");
  }, [open, initialData]);

  // Fetch accounts for ledger linking
  useEffect(() => {
    axiosRequest("GET", `${apiBaseUrl}/accounts`, token).then((r) => setAccounts(r?.data?.data || [])).catch(() => {});
  }, [token]);

  // Load user-specific data when initialUser changes
  useEffect(() => {
    if (!initialUser?._id || !open) return;
    const searchParam = initialUser.playerId
      ? `playerId=${initialUser.playerId}`
      : `phone=${initialUser.phone}`;
    axiosRequest("GET", `/user_details?${searchParam}`, token).then((res) => {
      if (!res?.data) return;
      setLiveUser(res.data?.userDetails || null);
      setIsFirstDepositor(!!res.data?.userDetails?.is_first);
      setLastOfferAt(res.data?.userLastOfferTxn?.createdAt || null);
      const referred = !!res.data?.userDetails?.isReferred;
      setUserIsReferred(referred);
      if (referred && res.data?.userReferral?.referralCode) {
        setReferralCode(res.data.userReferral.referralCode);
        setIsRefer(true);
      }
    });
  }, [initialUser?._id, open]);

  const handleChipChange = (value, qty) => {
    const updated = { ...chipDenominations, [value]: Number(qty) };
    const chipTotal = Object.keys(updated).reduce((s, k) => s + k * updated[k], 0);
    if (tournamentType !== "free" && type !== "wallet") {
      if (maxAllowedChips && chipTotal > maxAllowedChips) {
        getSweetAlert("error", `Maximum chips allowed ${maxAllowedChips}`);
        return;
      }
      if (!maxAllowedChips) {
        getSweetAlert("error", "Please enter the amount first");
        return;
      }
    }
    setChipDenominations(updated);
  };

  const handleReset = () => {
    setIsRefer(false);
    setReferralCode("");
    setAmount("");
    setUtr("");
    depositCategory === "tournament" ? tourChipDenominationRefetch() : chipDenominationRefetch();
    setSelectedOffer("");
    setOfferApproved(false);
    setChipDenominations({});
    setSelectedTournament("");
    setConfirmBtn(false);
    setDepositCategory("simple");
    setTournamentType("fee");
    setType("cash");
    setLiveUser(null);
    setRemark("");
    setRepayFloat(true);
    setSelectedAccountId("");
    onClose();
  };

  const handleSubmit = async () => {
    setConfirmBtn(true);
    if (type === "cash" && totalChips > maxAllowedChips) { getSweetAlert("error", `Maximum chips allowed ${maxAllowedChips}`); setConfirmBtn(false); return; }
    if (type === "cash" && totalCash <= 0) { getSweetAlert("error", "Enter deposit amount"); setConfirmBtn(false); return; }
    if (type === "bank" && !utr) { getSweetAlert("error", "Enter UTR Number"); setConfirmBtn(false); return; }
    if (type === "bank" && totalCash <= 0) { getSweetAlert("error", "Enter deposit amount"); setConfirmBtn(false); return; }
    if (type === "other" && totalCash <= 0) { getSweetAlert("error", "Enter deposit amount"); setConfirmBtn(false); return; }
    if (depositCategory === "tournament" && !selectedTournament) { getSweetAlert("error", "Please select a tournament"); setConfirmBtn(false); return; }

    const user = liveUser || initialUser;
    let payload = {
      user_id: user?._id,
      depositType: type,
      chipDenominations,
      totalCash,
      totalChips,
      offer_id: offerApproved ? selectedOffer : null,
      depositCategory,
      tournamentType,
      isRefer,
      referralCode: isRefer ? referralCode : "",
      amount,
      remark: (type === "cash" || type === "other") ? remark : undefined,
      account_id: selectedAccountId || undefined,
      repay_float: repayFloat,
    };
    if (depositCategory === "tournament" && tournamentType === "free") {
      payload = { user_id: user?._id, depositType: "", chipDenominations, totalChips, depositCategory, tournamentType, tournament_id: selectedTournament, account_id: selectedAccountId || undefined };
    }
    if (depositCategory === "tournament" && tournamentType === "fee") {
      payload = { user_id: user?._id, depositType: type, chipDenominations, totalCash, totalChips, offer_id: offerApproved ? selectedOffer : null, depositCategory, tournamentType, utr, amount, tournament_id: selectedTournament, account_id: selectedAccountId || undefined, repay_float: repayFloat };
    }

    const res = await axiosRequest("POST", "/deposit", token, payload);
    if (!res) { setConfirmBtn(false); return; }
    if (res?.data?.status) {
      const depositedAmount = Number(amount);
      const offerApprovalRequired = res.data.offer_approval_required;
      const pendingCashback = Number(res.data.cashback_amount || 0);
      const pendingTxnId = res.data.transaction_id;

      handleReset();
      chipDenominationRefetch();
      tourChipDenominationRefetch();
      if (onSuccess) onSuccess();

      if (offerApprovalRequired && pendingTxnId) {
        const approveResult = await Swal.fire({
          title: "Approve Cashback?",
          html: `Deposit successful.<br/>Cashback <b>${pendingCashback.toLocaleString("en-IN")}</b> is held. Approve now?`,
          icon: "question",
          showCancelButton: true,
          confirmButtonColor: "#2e7d32",
          confirmButtonText: "Approve",
          cancelButtonText: "Later (approve from Transactions)",
        });
        if (approveResult.isConfirmed) {
          const approveRes = await axiosRequest("PATCH", `${apiBaseUrl}/transaction/${pendingTxnId}/offer-approval`, token, { action: "APPROVE" });
          if (approveRes?.status === "success") {
            getSweetAlert("success", `Cashback ${pendingCashback.toLocaleString("en-IN")} approved!`);
          } else {
            getSweetAlert("error", approveRes?.message || "Approval failed");
          }
        }
      } else {
        getSweetAlert("success", res?.message || "Deposit successful");
      }

      if (user && depositedAmount > 0) {
        setChipIssueUser(user);
        setChipIssueAmount(depositedAmount);
        setChipIssueOpen(true);
      }
    } else {
      getSweetAlert("error", res?.data?.message || res?.message || "Deposit failed");
      setConfirmBtn(false);
    }
  };

  useEffect(() => {
    const base = totalCash;
    if (!selectedOffer || !offerApproved) {
      setCashback(0);
      setMaxAllowedChips(base);
      setOfferError("");
      return;
    }
    const offer = offers?.find(o => o._id === selectedOffer);
    if (!offer) return;
    if (base < offer.min_amount) { setOfferError(`Minimum deposit ${offer.min_amount} required`); setCashback(0); setMaxAllowedChips(base); return; }
    setOfferError("");
    let cb = offer.coupon_type === "FLAT" ? offer.max_cashback_amount : (base * offer.max_cashback_percent) / 100;
    if (cb > offer.max_cashback_amount) cb = offer.max_cashback_amount;
    setCashback(cb);
    setMaxAllowedChips(base + cb);
  }, [selectedOffer, offerApproved, totalCash]);

  useEffect(() => { setChipDenominations({}); }, [depositCategory]);

  const isFreeTourn = depositCategory === "tournament" && tournamentType === "free";

  return (
    <>
      <Dialog open={open} onClose={handleReset} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>

        {/* Header */}
        <Box sx={{ background: "linear-gradient(135deg,#1e3c72,#2a5298)", px: 3, py: 2.5, color: "#fff" }}>
          <Box display="flex" alignItems="center" gap={1.5}>
            <Avatar sx={{ bgcolor: "rgba(255,255,255,0.2)" }}><PersonIcon /></Avatar>
            <Box>
              <Typography variant="h6" fontWeight={700}>{liveUser?.name || initialUser?.name}</Typography>
              <Typography variant="caption" sx={{ opacity: 0.8 }}>Player ID: {liveUser?.playerId || initialUser?.playerId}</Typography>
            </Box>
            <Box ml="auto" display="flex" alignItems="center" gap={1.5}>
              <Box display="flex" alignItems="center" gap={0.5}>
                <AccountBalanceWalletIcon fontSize="small" sx={{ opacity: 0.8 }} />
                <Typography variant="body2" fontWeight={600}>{Number((liveUser || initialUser)?.winning_balance || 0).toLocaleString("en-IN")}</Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={0.5}>
                <AccountBalanceWalletIcon fontSize="small" sx={{ opacity: 0.8, color: "#ce93d8" }} />
                <Typography variant="body2" fontWeight={600} sx={{ color: "#ce93d8" }}>Promo: {Number((liveUser || initialUser)?.promotional_balance || 0).toLocaleString("en-IN")}</Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={0.5}>
                <AccountBalanceWalletIcon fontSize="small" sx={{ opacity: 0.8, color: "#90caf9" }} />
                <Typography variant="body2" fontWeight={600} sx={{ color: "#90caf9" }}>Deposited: {Number((liveUser || initialUser)?.cash_deposite || 0).toLocaleString("en-IN")}</Typography>
              </Box>
            </Box>
          </Box>
        </Box>

        <DialogContent sx={{ p: 3 }}>
          {(liveUser?.red_flag || initialUser?.red_flag) && (
            <Alert severity="error" icon={<FlagIcon />} sx={{ mb: 2, fontWeight: 600 }}>
              RED FLAG — {(liveUser?.red_flag_remark || initialUser?.red_flag_remark) || "This player has been flagged"}
            </Alert>
          )}
          {/* Float recovery — optional; wallet deposits never touch the float */}
          {floatDue > 0 && type !== "wallet" && !isFreeTourn && (
            <Box sx={{ mb: 2, px: 2, py: 1, borderRadius: 2, border: "1px solid", borderColor: repayFloat ? "#e65100" : "divider" }}>
              <FormControlLabel
                control={<Switch checked={repayFloat} onChange={(e) => setRepayFloat(e.target.checked)} color="warning" />}
                label={
                  <Box>
                    <Typography variant="body2" fontWeight={700}>Repay float from this deposit</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {repayFloat
                        ? `Float outstanding ${floatDue.toLocaleString("en-IN")} is repaid first${totalCash > 0 ? ` — ${Math.min(totalCash, floatDue).toLocaleString("en-IN")} goes to float` : ""}`
                        : `Float skipped — ${floatDue.toLocaleString("en-IN")} stays outstanding`}
                    </Typography>
                  </Box>
                }
              />
            </Box>
          )}
          <Grid container spacing={2.5}>

            {/* Category & Tournament */}
            <Grid item xs={12} sm={6}>
              <TextField select fullWidth size="small" label="Deposit Category" value={depositCategory}
                onChange={(e) => { setDepositCategory(e.target.value); setType(""); setChipDenominations({}); }}>
                <MenuItem value="simple">Simple Deposit</MenuItem>
                <MenuItem value="tournament">Tournament</MenuItem>
              </TextField>
            </Grid>

            {depositCategory === "tournament" && (
              <Grid item xs={12} sm={6}>
                <TextField select fullWidth size="small" label="Tournament Type" value={tournamentType}
                  onChange={(e) => { setTournamentType(e.target.value); setType(e.target.value === "fee" ? "cash" : ""); setChipDenominations({}); }}>
                  <MenuItem value="free">Free Tournament</MenuItem>
                  <MenuItem value="fee">Fee Based Tournament</MenuItem>
                </TextField>
              </Grid>
            )}

            {depositCategory === "tournament" && (
              <Grid item xs={12} sm={6}>
                <TextField select fullWidth size="small" label="Select Tournament" value={selectedTournament}
                  onChange={(e) => setSelectedTournament(e.target.value)}>
                  <MenuItem value="">Select Tournament</MenuItem>
                  {tournaments?.map((t) => <MenuItem key={t._id} value={t._id}>{t.name}</MenuItem>)}
                </TextField>
              </Grid>
            )}

            {/* Payment Section */}
            {!isFreeTourn && (
              <>
                <Grid item xs={12}><Divider><Chip label="Payment Details" size="small" /></Divider></Grid>

                <Grid item xs={12} sm={6}>
                  <TextField select fullWidth size="small" label="Deposit Type" value={type}
                    onChange={(e) => { setType(e.target.value); setChipDenominations({}); }}>
                    <MenuItem value="cash">Cash Deposit</MenuItem>
                    <MenuItem value="bank">Bank Transfer</MenuItem>
                    <MenuItem value="other">Other</MenuItem>
                  </TextField>
                </Grid>

                {/* Amount field for cash, bank and other */}
                {(type === "cash" || type === "bank" || type === "other") && (
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth size="small" type="number" label="Amount ()" value={amount}
                      onChange={(e) => setAmount(e.target.value)} inputProps={{ min: 0 }} />
                  </Grid>
                )}

                {/* Remark for cash / other deposit */}
                {(type === "cash" || type === "other") && (
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth size="small" label="Remark" value={remark} onChange={(e) => setRemark(e.target.value)} placeholder="Optional remark" />
                  </Grid>
                )}

                {/* UTR for bank */}
                {type === "bank" && (
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth size="small" label="UTR Number" value={utr} onChange={(e) => setUtr(e.target.value)} />
                  </Grid>
                )}

                {type !== "wallet" && (
                  <Grid item xs={12} sm={6}>
                    <TextField select fullWidth size="small" label="Payment Offer" value={selectedOffer}
                      onChange={(e) => {
                        const val = e.target.value;
                        setSelectedOffer(val);
                        const within24h = !!lastOfferAt && (Date.now() - new Date(lastOfferAt).getTime()) < 24 * 60 * 60 * 1000;
                        setOfferApproved(val ? !within24h : false);
                      }}>
                      <MenuItem value="">No Offer</MenuItem>
                      {offers
                        ?.filter((o) => {
                          if (o.status !== 1) return false;
                          if (o.expiry_date) {
                            const exp = new Date(o.expiry_date);
                            exp.setHours(23, 59, 59, 999);
                            if (exp < new Date()) return false;
                          }
                          if (o.first_deposite == 1 && !isFirstDepositor) return false;
                          return true;
                        })
                        .map((o) => (
                          <MenuItem key={o._id} value={o._id}>
                            {o.coupon_code} {o.max_cashback_percent > 0 ? `(${o.max_cashback_percent}%)` : `(Flat ${o.max_cashback_amount})`}
                            {o.first_deposite == 1 ? " — 1st Deposit" : ""}
                          </MenuItem>
                        ))}
                    </TextField>
                  </Grid>
                )}

                {type !== "wallet" && selectedOffer && (() => {
                  const within24h = !!lastOfferAt && (Date.now() - new Date(lastOfferAt).getTime()) < 24 * 60 * 60 * 1000;
                  if (!within24h) {
                    return (
                      <Grid item xs={12} sm={6} display="flex" alignItems="center">
                        <Box sx={{ px: 1.5, py: 1, borderRadius: 2, background: "#f0fdf4", border: "1px solid #4caf50", width: "100%" }}>
                          <Typography variant="body2" fontWeight={700} color="#2e7d32">
                            ✅ Offer will be applied automatically — no approval needed
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {!lastOfferAt ? "No previous offer deposit found" : "Last offer was more than 24 hours ago"}
                          </Typography>
                        </Box>
                      </Grid>
                    );
                  }
                  return (
                    <Grid item xs={12} sm={6} display="flex" flexDirection="column" gap={1}>
                      <Box sx={{ px: 1.5, py: 1, borderRadius: 2, background: "#fff3e0", border: "1px solid #ffb300" }}>
                        <Typography variant="caption" fontWeight={700} color="#e65100">
                          ⏱ Last offer was within 24 hours — cashback will be HELD pending supervisor approval
                        </Typography>
                      </Box>
                      <Box sx={{ px: 1.5, py: 0.75, borderRadius: 2, border: "2px solid #e65100", background: offerApproved ? "#fff3e0" : "#fff5f5", display: "flex", alignItems: "center" }}>
                        <FormControlLabel
                          control={
                            <Checkbox size="small" checked={offerApproved} onChange={(e) => setOfferApproved(e.target.checked)}
                              sx={{ color: "#e65100", "&.Mui-checked": { color: "#bf360c" } }} />
                          }
                          label={<Typography variant="body2" fontWeight={700} color="#bf360c">⚠️ I confirm — cashback will be held for supervisor approval</Typography>}
                          sx={{ m: 0 }}
                        />
                      </Box>
                    </Grid>
                  );
                })()}

                {offerError && (
                  <Grid item xs={12}>
                    <Typography color="error" variant="caption">{offerError}</Typography>
                  </Grid>
                )}

                {selectedOffer && offerApproved && !offerError && (
                  <Grid item xs={12}>
                    <Box display="flex" gap={2} sx={{ p: 1.5, borderRadius: 2, background: "#f0fdf4", border: "1px solid #bbf7d0" }}>
                      <Box><Typography variant="caption" color="text.secondary">Cashback</Typography><Typography fontWeight={700} color="success.main">{cashback}</Typography></Box>
                      <Divider orientation="vertical" flexItem />
                      <Box><Typography variant="caption" color="text.secondary">Max Chips Allowed</Typography><Typography fontWeight={700}>{maxAllowedChips}</Typography></Box>
                    </Box>
                  </Grid>
                )}

                {type !== "wallet" && userIsReferred && referralCode && (
                  <>
                    <Grid item xs={12} sm={6}>
                      <FormControlLabel
                        control={<Checkbox size="small" checked={isRefer} onChange={(e) => setIsRefer(e.target.checked)} />}
                        label="Mark as Referral"
                      />
                    </Grid>
                    {isRefer && (
                      <Grid item xs={12} sm={6}>
                        <TextField fullWidth size="small" label="Referral Code" value={referralCode}
                          InputProps={{ readOnly: true }}
                          sx={{ "& .MuiInputBase-input": { color: "success.main", fontWeight: 600 } }} />
                      </Grid>
                    )}
                  </>
                )}

                {initialUser && (!userIsReferred || !referralCode) && (
                  <>
                    <Grid item xs={12} sm={6}>
                      <FormControlLabel
                        control={<Checkbox size="small" checked={isRefer} onChange={(e) => setIsRefer(e.target.checked)} />}
                        label="Mark as Referral"
                      />
                    </Grid>
                    {isRefer && (
                      <Grid item xs={12} sm={6}>
                        <Box display="flex" gap={1} alignItems="center">
                          <TextField fullWidth size="small" label="Referral Code" placeholder="Enter referral code"
                            value={referralCode} onChange={(e) => setReferralCode(e.target.value.toUpperCase())} />
                          <Button variant="contained" color="secondary" size="small" disabled={!referralCode.trim()}
                            onClick={async () => {
                              const res = await axiosRequest("POST", "/apply-referral", token, { user_id: (liveUser || initialUser)?._id, referralCode });
                              if (res?.data?.status === true) {
                                getSweetAlert("success", res?.data?.message || "Referral applied!");
                                setUserIsReferred(true);
                                setIsRefer(true);
                              } else {
                                getSweetAlert("error", res?.data?.message || "Invalid referral code");
                              }
                            }}
                            sx={{ whiteSpace: "nowrap", minWidth: 80 }}>
                            Apply
                          </Button>
                        </Box>
                      </Grid>
                    )}
                  </>
                )}
              </>
            )}

          </Grid>
        </DialogContent>

        {/* Account Ledger */}
        {accounts.length > 0 && (
          <Box sx={{ px: 3, pb: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Link to Account (optional)</InputLabel>
              <Select value={selectedAccountId} label="Link to Account (optional)" onChange={(e) => setSelectedAccountId(e.target.value)}>
                <MenuItem value="">— None —</MenuItem>
                {accounts.map((a) => <MenuItem key={a._id} value={a._id}>{a.name}{a.phone ? ` — ${a.phone}` : ""}</MenuItem>)}
              </Select>
            </FormControl>
          </Box>
        )}

        {/* Footer */}
        <Box sx={{ px: 3, py: 2, borderTop: "1px solid #e0e0e0", display: "flex", justifyContent: "space-between", alignItems: "center", background: "#fafafa" }}>
          <Box display="flex" gap={1}>
            {type && <Chip label={type.toUpperCase()} color="primary" size="small" variant="outlined" />}
            {depositCategory === "tournament" && <Chip label="Tournament" color="secondary" size="small" variant="outlined" />}
            {totalCash > 0 && <Chip label={`${totalCash.toLocaleString("en-IN")}`} color="success" size="small" variant="outlined" />}
          </Box>
          <Box display="flex" gap={1.5}>
            <Button onClick={handleReset} variant="outlined" color="inherit" sx={{ borderRadius: 2 }}>Cancel</Button>
            <Button variant="contained" color="success" onClick={handleSubmit} disabled={confirmBtn}
              sx={{ borderRadius: 2, fontWeight: 700, minWidth: 140 }}>
              {confirmBtn ? "Processing…" : "Confirm Deposit"}
            </Button>
          </Box>
        </Box>

      </Dialog>

      <RedFlagPopup user={open ? (liveUser || initialUser) : null} />
      <FloatPopup user={open ? (liveUser || initialUser) : null} context="deposit" />

      <ChipIssueForm
        open={chipIssueOpen}
        onClose={() => { setChipIssueOpen(false); setChipIssueUser(null); setChipIssueAmount(0); }}
        refresh={() => { if (onSuccess) onSuccess(); }}
        initialUser={chipIssueUser}
        initialAmount={chipIssueAmount}
        hideRedFlagPopup
      />
    </>
  );
}
